import {
  Button,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Select,
  Space,
} from "antd";
import dayjs from "dayjs";
import { useEffect } from "react";
import ReactQuill from "react-quill";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useAppSelector } from "../../../app/store";
import BackButtonIcon from "../../../assets/icons/BackButtonIcon";
import DateIcon from "../../../assets/icons/DateIcon";
import LinkIcon from "../../../assets/icons/LinkIcon";
import SelectDropdownLgIcon from "../../../assets/icons/SelectDropdownLgIcon";
import TopCard from "../../../components/common/TopCard";
import Loader from "../../../components/ui/Loader";
import {
  quillToolbarFormats,
  quillToolbarModules,
} from "../../../constants/config";
import { dbDateFormat } from "../../../constants/constants";
import {
  useAddJobMutation,
  useGetJobDetailsQuery,
  useUpdateJobMutation,
} from "../../../features/employee/employeeApi";
import useError from "../../../hooks/useError";
import SidebarLayout from "../../../layouts/SidebarLayout";
import { disableFutureDates, disablePastDates } from "../../../lib/time";
import { toast } from "../../../lib/toast";
import { selectSearchOption } from "../../../utils/utils";
import { EType, JType } from "./JobPost";

const backLink = "/admin/recruitment/job-post";

export default function ManageJob() {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { id } = useParams();
  const { authInfo } = useAppSelector((state) => state.auth);
  const [addJob, { isLoading, isError, error, isSuccess }] =
    useAddJobMutation();
  const [
    updateJob,
    {
      isLoading: isUpdateLoading,
      isError: isUpdateError,
      error: updateError,
      isSuccess: isUpdateSuccess,
    },
  ] = useUpdateJobMutation();
  const {
    isLoading: isViewLoading,
    isError: isViewError,
    error: viewError,
    data: detailsData,
  } = useGetJobDetailsQuery(id!, {
    skip: !id,
  });
  useError(isError, error, "Job create fail");
  useError(isUpdateError, updateError, "Job update fail");
  useError(isViewError, viewError, "Get job details fail");

  const details = detailsData?.data;

  const onFinish = (values: any) => {
    const {
      deadline,
      company_founded,
      company_no_of_employees,
      experience_year,
      ...rest
    } = values;

    const newValue = {
      ...rest,
      business_id: authInfo?.business_id,
      deadline: deadline ? deadline?.format(dbDateFormat) : undefined,
      company_founded: company_founded
        ? company_founded?.format(dbDateFormat)
        : undefined,
      company_no_of_employees: String(company_no_of_employees),
      experience_year: String(experience_year),
    };

    if (id) {
      updateJob({
        id: String(id),
        body: { ...newValue, status: details?.status },
      });
    } else {
      addJob({ ...newValue, status: "active" });
    }
  };

  useEffect(() => {
    if (Object.keys(details || {}).length) {
      form.setFieldsValue({
        job_title: details?.job_title,
        job_location: details?.job_location,
        employment_status: details?.employment_status,
        job_salary: details?.job_salary,
        gender: details?.gender,
        age_range: details?.age_range,
        experience_year: Number(details?.experience_year),
        employment_type: details?.employment_type,
        no_of_vacancies: details?.no_of_vacancies,
        deadline: details?.deadline
          ? dayjs(details?.deadline, dbDateFormat)
          : undefined,
        job_context: details?.job_context,
        job_description: details?.job_description,
        educational_requirements: details?.educational_requirements,
        experience_requirements: details?.experience_requirements,
        additional_requirements: details?.additional_requirements,
        compensation_benefits: details?.compensation_benefits,
        company_website: details?.company_website,
        company_no_of_employees: details?.company_no_of_employees
          ? Number(details?.company_no_of_employees)
          : undefined,
        company_founded: details?.company_founded
          ? dayjs(details?.company_founded, dbDateFormat)
          : undefined,
        company_how_to_receive_application:
          details?.company_how_to_receive_application,
        company_about: details?.company_about,
      });
    }
  }, [details, form]);

  useEffect(() => {
    if (isSuccess) {
      toast("success", "Job created successfully");
      navigate(backLink);
    }
  }, [isSuccess, navigate]);

  useEffect(() => {
    if (isUpdateSuccess) {
      toast("success", "Job updated successfully");
      navigate(backLink);
    }
  }, [isUpdateSuccess, navigate]);

  return (
    <SidebarLayout title={`${id ? "Update" : "Add"} Job Post`}>
      <TopCard
        size={16}
        align="center"
        leftChildren={
          <Link to={backLink}>
            <Button className="normal-dark-button" icon={<BackButtonIcon />}>
              Back
            </Button>
          </Link>
        }
      >
        {/* <Button icon={<SettingIcon />} type="primary" className="sm-button" /> */}
      </TopCard>

      {isViewLoading ? (
        <Loader />
      ) : (
        <div className="bg-white rounded-md border border-extra-light p-4">
          <Form
            className="custom-form"
            layout="vertical"
            requiredMark="optional"
            size="large"
            onFinish={onFinish}
            form={form}
          >
            <div className="grid md:grid-cols-2 gap-x-4">
              <Form.Item
                name="job_title"
                label="Job Title"
                className="mb-4"
                rules={[
                  {
                    required: true,
                    message: "Please enter job title",
                  },
                ]}
              >
                <Input size="large" placeholder="Type job title" />
              </Form.Item>
              <Form.Item
                name="job_location"
                label="Job Location"
                className="mb-4"
                rules={[
                  {
                    required: true,
                    message: "Please enter job location",
                  },
                ]}
              >
                <Input size="large" placeholder="Type job location" />
              </Form.Item>
              <Form.Item
                name="employment_status"
                label="Employment Type"
                className="mb-4"
                rules={[
                  {
                    required: true,
                    message: "Please enter employment type",
                  },
                ]}
              >
                <Select
                  placeholder="Select..."
                  size="large"
                  allowClear
                  showSearch
                  filterOption={selectSearchOption}
                  options={Object.entries(EType).map(([value, label]) => ({
                    label,
                    value,
                  }))}
                  suffixIcon={<SelectDropdownLgIcon />}
                />
              </Form.Item>
              <Form.Item
                name="job_salary"
                label="Salary"
                className="mb-4"
                validateFirst
                rules={[
                  {
                    validator: (_, value) => {
                      if (!value) {
                        return Promise.resolve();
                      }

                      const convertedValue = Number(value);

                      if (!isNaN(convertedValue) && convertedValue < 0) {
                        return Promise.reject(
                          new Error("Salary can't be negative")
                        );
                      }

                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <Input
                  className="w-full"
                  size="large"
                  placeholder="Enter salary"
                />
              </Form.Item>

              <Form.Item
                name="gender"
                label="Gender"
                className="mb-4"
                rules={[
                  {
                    required: true,
                    message: "Please enter gender",
                  },
                ]}
              >
                <Select
                  placeholder="Select..."
                  size="large"
                  allowClear
                  showSearch
                  filterOption={selectSearchOption}
                  options={jobGenderList}
                  suffixIcon={<SelectDropdownLgIcon />}
                />
              </Form.Item>

              <Form.Item
                name="age_range"
                label="Age Range"
                className="mb-4"
                rules={[
                  {
                    required: true,
                    message: "Please enter age range",
                  },
                ]}
              >
                <Input size="large" placeholder="Enter age range" />
              </Form.Item>

              <Form.Item
                name="experience_year"
                label="Experience Years"
                className="mb-4"
                validateFirst
                rules={[
                  {
                    required: true,
                    message: "Please enter experience years",
                  },
                  {
                    validator: (_, value) => {
                      if (!value) {
                        return Promise.resolve();
                      }

                      const numberValue = Number(value);
                      if (typeof value === "string" && value.includes(" ")) {
                        return Promise.reject(
                          new Error("Spaces are not allowed")
                        );
                      }
                      if (isNaN(numberValue)) {
                        return Promise.reject(
                          new Error("Experience must be a number")
                        );
                      }

                      if (numberValue < 0) {
                        return Promise.reject(
                          new Error("Experience can't be negative")
                        );
                      }

                      if (numberValue > 50) {
                        return Promise.reject(
                          new Error("Experience can't be more than 50 years")
                        );
                      }

                      // Check for more than 1 digit after the decimal
                      if (!/^\d+(\.\d{1})?$/.test(value)) {
                        return Promise.reject(
                          new Error(
                            "Only one digit is allowed after the decimal point"
                          )
                        );
                      }

                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <InputNumber className="w-full" size="large" placeholder="0" />
              </Form.Item>

              <Form.Item
                name="employment_type"
                label="Job Type"
                className="mb-4"
                rules={[
                  {
                    required: true,
                    message: "Please select job type",
                  },
                ]}
              >
                <Select
                  placeholder="Select..."
                  size="large"
                  allowClear
                  showSearch
                  filterOption={selectSearchOption}
                  options={Object.entries(JType).map(([value, label]) => ({
                    label,
                    value,
                  }))}
                  suffixIcon={<SelectDropdownLgIcon />}
                />
              </Form.Item>

              <Form.Item
                name="no_of_vacancies"
                label="Vacancy"
                className="mb-4"
                validateFirst
                initialValue={1}
                rules={[
                  {
                    required: true,
                    message: "Please enter vacancy",
                  },
                  {
                    validator: (_, value) => {
                      if (
                        value === undefined ||
                        value === null ||
                        value === ""
                      ) {
                        return Promise.resolve();
                      }

                      if (value < 0) {
                        return Promise.reject(
                          new Error("Vacancy can't be negative")
                        );
                      }

                      if (!Number.isInteger(Number(value))) {
                        return Promise.reject(
                          new Error("Vacancy must be a whole number")
                        );
                      }

                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <InputNumber className="w-full" size="large" placeholder="0" />
              </Form.Item>

              <Form.Item
                name="deadline"
                label="Deadline"
                className="mb-4"
                rules={[
                  {
                    required: true,
                    message: "Please select deadline",
                  },
                ]}
              >
                <DatePicker
                  format={"DD/MM/YYYY"}
                  suffixIcon={<DateIcon width={18} height={20} />}
                  style={{ width: "100%" }}
                  size="large"
                  placeholder="dd/mm/yyyy"
                  disabledDate={disablePastDates}
                />
              </Form.Item>

              <Form.Item
                name="job_context"
                label="Job Context"
                className="pb-11"
                // rules={[
                //   {
                //     required: true,
                //     message: "Please select job context",
                //   },
                // ]}
              >
                <ReactQuill
                  className="h-[150px] mb-11"
                  theme="snow"
                  formats={quillToolbarFormats}
                  modules={quillToolbarModules}
                  placeholder="Type Job Context..."
                />
              </Form.Item>

              <Form.Item
                name="job_description"
                label="Job Description"
                className="pb-11"
                rules={[
                  {
                    required: true,
                    message: "Please enter job description",
                  },
                ]}
              >
                <ReactQuill
                  className="h-[150px] mb-11"
                  theme="snow"
                  formats={quillToolbarFormats}
                  modules={quillToolbarModules}
                  placeholder="Type Job Description..."
                />
              </Form.Item>

              <Form.Item
                name="educational_requirements"
                label="Educational Requirements"
                className="pb-11"
                // rules={[
                //   {
                //     required: true,
                //     message: "Please enter educational requirements",
                //   },
                // ]}
              >
                <ReactQuill
                  className="h-[150px] mb-11"
                  theme="snow"
                  formats={quillToolbarFormats}
                  modules={quillToolbarModules}
                  placeholder="Type Educational Requirements"
                />
              </Form.Item>
              <Form.Item
                name="experience_requirements"
                label="Experience Requirements"
                className="pb-11"
                // rules={[
                //   {
                //     required: true,
                //     message: "Please enter experience requirements",
                //   },
                // ]}
              >
                <ReactQuill
                  className="h-[150px] mb-11"
                  theme="snow"
                  formats={quillToolbarFormats}
                  modules={quillToolbarModules}
                  placeholder="Type Experience Requirements"
                />
              </Form.Item>
              <Form.Item
                name="additional_requirements"
                label="Additional Requirements"
                className="pb-11"
                // rules={[
                //   {
                //     required: true,
                //     message: "Please enter additional requirements",
                //   },
                // ]}
              >
                <ReactQuill
                  className="h-[150px] mb-11"
                  theme="snow"
                  formats={quillToolbarFormats}
                  modules={quillToolbarModules}
                  placeholder="Type Additional Requirements"
                />
              </Form.Item>
              <Form.Item
                name="compensation_benefits"
                label="Compensation & Other Benefits "
                className="pb-11"
                // rules={[
                //   {
                //     required: true,
                //     message: "Please enter compensation other benefits",
                //   },
                // ]}
              >
                <ReactQuill
                  className="h-[150px] mb-11"
                  theme="snow"
                  formats={quillToolbarFormats}
                  modules={quillToolbarModules}
                  placeholder="Type Compensation Other Benefits"
                />
              </Form.Item>

              <div className="md:col-span-2 px-3 py-1.5 mb-4 bg-status-dark-bg rounded-md text-display-body text-lg font-semibold">
                Company Information
              </div>

              {/*<Form.Item
                name="company_name"
                label="Company Name"
                className="mb-4"
                rules={[
                  {
                    required: true,
                    message: "Please enter company name",
                  },
                ]}
              >
                <Input size="large" placeholder="Enter company name" />
              </Form.Item>*/}

              <Form.Item
                name="company_website"
                label="Company Website"
                className="mb-4"
                // rules={[
                //   {
                //     required: true,
                //     message: "Please enter company website",
                //   },
                // ]}
              >
                <Input size="large" placeholder="https://" />
              </Form.Item>
              <Form.Item
                name="company_no_of_employees"
                label="Number of employees"
                className="mb-4"
                initialValue={1}
                rules={[
                  {
                    validator: (_, value) => {
                      if (
                        value === undefined ||
                        value === null ||
                        value === ""
                      ) {
                        return Promise.resolve();
                      }

                      if (value < 0) {
                        return Promise.reject(
                          new Error("Vacancy can't be negative")
                        );
                      }
                      if (value === 0) {
                        return Promise.reject(
                          new Error("Vacancy must be more then one")
                        );
                      }

                      if (!Number.isInteger(Number(value))) {
                        return Promise.reject(
                          new Error("Vacancy must be a whole number")
                        );
                      }

                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <InputNumber className="w-full" size="large" placeholder="0" />
              </Form.Item>

              <Form.Item
                name="company_founded"
                label="Founded"
                className="mb-4"
                rules={[
                  {
                    required: true,
                    message: "Please select founded",
                  },
                ]}
              >
                <DatePicker
                  format={"DD/MM/YYYY"}
                  suffixIcon={<DateIcon width={18} height={20} />}
                  style={{ width: "100%" }}
                  size="large"
                  placeholder="dd/mm/yyyy"
                  disabledDate={disableFutureDates}
                />
              </Form.Item>

              {/* <Form.Item
                name="company_how_to_receive_application"
                label="How to receive applications"
                className="mb-4"
                validateFirst
                rules={[
                  // {
                  //   required: true,
                  //   message: "Please enter email",
                  // },
                  {
                    validator: async (_: any, value: string) => {
                      if (!value) {
                        return Promise.resolve();
                      }

                      if (!mailRegex.test(value)) {
                        return Promise.reject(
                          new Error("Please enter a valid email")
                        );
                      }

                      if (!isValidTLD(value)) {
                        return Promise.reject(
                          new Error("Please enter a valid TLD")
                        );
                      }

                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <Input size="large" placeholder="Enter email address" />
              </Form.Item> */}

              <Form.Item
                name="company_about"
                // label="Experience Requirements"
                className="pb-0 md:col-span-2"
                // rules={[
                //   {
                //     required: true,
                //     message: "Please enter about your company",
                //   },
                // ]}
              >
                <ReactQuill
                  className="h-[150px] mb-11"
                  theme="snow"
                  formats={quillToolbarFormats}
                  modules={quillToolbarModules}
                  placeholder="About your company"
                />
              </Form.Item>
            </div>

            <Space className="w-full" size={12}>
              {/* <Link to={backLink}>
                <Button
                  className="normal-dark-button modal-button"
                  size="middle"
                >
                  Cancel
                </Button>
              </Link> */}
              <Button
                className="modal-button !bg-[#1B4DFF] hover:!bg-[#1B4DFF]/90 disabled:!bg-[#1B4DFF]/90"
                type="primary"
                htmlType="submit"
                size="middle"
                icon={<LinkIcon />}
                loading={isLoading || isUpdateLoading}
              >
                {id ? "Update" : "Save & Copy link"}
              </Button>
            </Space>
          </Form>
        </div>
      )}
    </SidebarLayout>
  );
}

export const jobGenderList = [
  {
    label: "Male",
    value: "male",
  },
  {
    label: "Female",
    value: "female",
  },
  {
    label: "Both",
    value: "both",
  },
  {
    label: "Others",
    value: "others",
  },
];
